import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Title, useNotify } from "react-admin";

import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";

type BankDetails = {
  bankName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  ifscCode?: string | null;
  branchName?: string | null;
  upiId?: string | null;
  notes?: string | null;
};

type LedgerType = {
  id: string;
  code: string;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
  bankDetails?: BankDetails | null;
};

const emptyBankDetails = (): BankDetails => ({
  bankName: "",
  accountName: "",
  accountNumber: "",
  ifscCode: "",
  branchName: "",
  upiId: "",
  notes: "",
});

export default function YearlyLedgerSettingsPage() {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [types, setTypes] = useState<LedgerType[]>([]);
  const [drafts, setDrafts] = useState<Record<string, LedgerType>>({});
  const [newType, setNewType] = useState({ code: "", name: "" });

  const load = useCallback(() => {
    setLoading(true);
    httpClient(`${getApiUrl()}/yearly-ledger/types?includeInactive=true`)
      .then(({ json }) => {
        const body = json as { rows?: LedgerType[] };
        const rows = body.rows ?? [];
        setTypes(rows);
        setDrafts(
          Object.fromEntries(
            rows.map((row) => [
              row.id,
              {
                ...row,
                bankDetails: { ...emptyBankDetails(), ...(row.bankDetails ?? {}) },
              },
            ])
          )
        );
      })
      .catch(() => notify("Could not load yearly ledger types", { type: "error" }))
      .finally(() => setLoading(false));
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (id: string, patch: Partial<LedgerType>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const updateBankDraft = (id: string, patch: Partial<BankDetails>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        bankDetails: { ...emptyBankDetails(), ...(prev[id]?.bankDetails ?? {}), ...patch },
      },
    }));
  };

  const saveType = (id: string) => {
    const draft = drafts[id];
    if (!draft) return;
    setSavingId(id);
    httpClient(`${getApiUrl()}/yearly-ledger/types/${id}`, {
      method: "PUT",
      body: JSON.stringify({
        name: draft.name,
        sortOrder: draft.sortOrder ?? 0,
        isActive: draft.isActive ?? true,
        bankDetails: draft.bankDetails,
      }),
    })
      .then(() => {
        notify("Saved", { type: "success" });
        load();
      })
      .catch(() => notify("Save failed", { type: "error" }))
      .finally(() => setSavingId(null));
  };

  const createType = () => {
    if (!newType.code.trim() || !newType.name.trim()) {
      notify("Code and name are required", { type: "warning" });
      return;
    }
    setCreating(true);
    httpClient(`${getApiUrl()}/yearly-ledger/types`, {
      method: "POST",
      body: JSON.stringify({
        code: newType.code.trim(),
        name: newType.name.trim(),
        isActive: true,
      }),
    })
      .then(() => {
        notify("Ledger type created", { type: "success" });
        setNewType({ code: "", name: "" });
        load();
      })
      .catch(() => notify("Create failed", { type: "error" }))
      .finally(() => setCreating(false));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      <Title title="Yearly Ledger Settings" />
      <Box sx={{ p: 2, maxWidth: 900 }}>
        <Typography variant="h6" gutterBottom>
          Ledger types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ledger types are configured here and loaded by the dashboard. Bank details are optional
          and shown on the ledger dashboard when set.
        </Typography>

        {types.map((type) => {
          const draft = drafts[type.id];
          if (!draft) return null;
          const bank = draft.bankDetails ?? emptyBankDetails();
          return (
            <Card key={type.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {type.code}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Display name"
                      value={draft.name}
                      onChange={(e) => updateDraft(type.id, { name: e.target.value })}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label="Sort order"
                      type="number"
                      value={draft.sortOrder ?? 0}
                      onChange={(e) =>
                        updateDraft(type.id, { sortOrder: Number(e.target.value) || 0 })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }} sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.isActive ?? true}
                          onChange={(e) => updateDraft(type.id, { isActive: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Bank details (optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Bank name"
                      value={bank.bankName ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { bankName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Branch"
                      value={bank.branchName ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { branchName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Account name"
                      value={bank.accountName ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { accountName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Account number"
                      value={bank.accountNumber ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { accountNumber: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="IFSC"
                      value={bank.ifscCode ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { ifscCode: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="UPI ID"
                      value={bank.upiId ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { upiId: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Notes"
                      value={bank.notes ?? ""}
                      onChange={(e) => updateBankDraft(type.id, { notes: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => saveType(type.id)}
                    disabled={savingId === type.id}
                  >
                    {savingId === type.id ? "Saving…" : "Save"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}

        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Add ledger type
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Code"
                  value={newType.code}
                  onChange={(e) => setNewType((prev) => ({ ...prev, code: e.target.value }))}
                  helperText="Letters, numbers, underscores"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Display name"
                  value={newType.name}
                  onChange={(e) => setNewType((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex", alignItems: "center" }}>
                <Button variant="outlined" onClick={createType} disabled={creating}>
                  {creating ? "Creating…" : "Add type"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
